import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Loader2, Building2, GraduationCap, ArrowRight, CheckCircle2, MailCheck, LogIn, Baby, HeartHandshake } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const PITCH = {
  business: {
    Icon: Building2, tag: "Built for businesses",
    headline: "Know exactly who's in your building — at all times.",
    blurb: "Legally compliant visitor & contractor sign-in, staff time clock with face check, and audit-ready reports — set up in 60 seconds.",
    bullets: ["14-day free trial — no credit card needed", "Unlimited visitors, contractors and check-ins", "Staff timesheets with anti-buddy-punch face verification", "Officer accountability + CSV/PDF audit exports"],
  },
  school: {
    Icon: GraduationCap, tag: "Built for schools",
    headline: "Every student accounted for — from drop-off to dismissal.",
    blurb: "A touch-friendly kiosk for drop-offs, tardies and verified pickups, with automatic parent alerts and audit-ready records.",
    bullets: ["14-day free trial — no credit card needed", "ID-verified pickups — only authorized adults, every rejection logged", "Parents notified automatically on absences and tardies", "Staff clock-in with face verification + roster sync with your SIS"],
  },
  care: {
    Icon: HeartHandshake, tag: "Built for personal care homes & adult care",
    headline: "Every client's stay recorded — arrival to signed hand-off.",
    blurb: "Employees check clients in and out with escort signatures, log meals, activities and incidents, and export monthly stay hours — plus visitor sign-in and staff time clock.",
    bullets: ["14-day free trial — no credit card needed", "Client roster with care needs, photos and authorized escorts", "PIN-protected check-in/out with escort e-signature", "Stay notes (meals, vitals, incidents) and monthly hours per client"],
  },
  daycare: {
    Icon: Baby, tag: "Built for daycares & Pre-K centers",
    headline: "Every child signed in, every moment shared with parents.",
    blurb: "Parents sign in with a family PIN at the kiosk, teachers log meals, naps, diapers and photos in a tap, and families follow the day live — with incident reports, health profiles and tuition billing built in.",
    bullets: ["14-day free trial — no credit card needed", "Family PIN sign-in/out with live room ratios", "Daily logs & photos delivered to the parent app in real time", "Boo-boo reports with parent e-signature, allergy & medication profiles", "Weekly or monthly tuition with card payments and automatic receipts"],
  },
};

export default function RegisterPage() {
  const [form, setForm] = useState({
    company_name: "",
    company_type: "business",
    admin_name: "",
    admin_email: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { refresh } = useAuth();
  const nav = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const pitch = PITCH[form.company_type] || PITCH.business;

  const [sent, setSent] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, admin_password: "", origin_url: window.location.origin });
      if (data?.token) {
        localStorage.setItem("vlog_token", data.token);
        await refresh();
        nav("/admin");
        return;
      }
      setSent(data);
      if (!data.verify_email_sent) toast.error(data.email_error || "We couldn't send the verification email. Use 'Forgot password' on the login page to get a link.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy-hero flex flex-col" data-testid="register-page">
      <div className="px-8 py-6 flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-slate-700">
          <img src="/logerd-logo.png" alt="" className="h-7 w-7 object-contain" />
          <span className="text-xs uppercase tracking-widest">Create your account</span>
        </div>
        <Link to="/login" data-testid="register-signin-btn" className="inline-flex items-center gap-2 rounded-full border border-sky-300/60 bg-white/80 backdrop-blur px-4 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-primary hover:text-white transition">
          <LogIn className="h-4 w-4" /> Sign in
        </Link>
      </div>

      <div className="flex flex-1 items-center justify-center px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 max-w-4xl w-full gap-8 rounded-2xl border border-slate-200 bg-white shadow-xl shadow-sky-900/15 overflow-hidden">
          {/* Left: marketing */}
          <div className="p-10 bg-gradient-to-br from-primary/5 via-white to-cyan-50/50 border-b lg:border-b-0 lg:border-r border-slate-200" data-testid={`register-pitch-${form.company_type}`}>
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-[0.25em] mb-4">
              <pitch.Icon className="h-3.5 w-3.5" />
              {pitch.tag}
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 leading-tight" data-testid="register-headline">{pitch.headline}</h1>
            <p className="mt-3 text-sm text-slate-600">{pitch.blurb}</p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {pitch.bullets.map((line) => (
                <li key={line} className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-2 text-xs text-slate-500">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Works on any tablet — install the kiosk in minutes, cancel anytime.
            </div>
          </div>

          {/* Right: form */}
          <div className="p-10">
            {sent ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center space-y-3" data-testid="register-check-email">
                <MailCheck className="h-10 w-10 text-emerald-600 mx-auto" />
                <div className="font-display text-xl font-bold text-slate-900">Check your email</div>
                <p className="text-sm text-slate-600">
                  We sent a link to <b data-testid="register-sent-email">{sent.email}</b>. Click it to verify your email and choose your password — then you're in. The link is valid for 24 hours.
                </p>
                <p className="text-xs text-slate-500">Didn't get it? Check your spam folder, or use <Link to="/forgot-password" className="text-primary underline">Forgot password</Link> with the same email to get a new link.</p>
              </div>
            ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="register-form">
              <Field label="Company / Building name">
                <input
                  required
                  value={form.company_name}
                  onChange={set("company_name")}
                  data-testid="register-company"
                  className={inputCls}
                  placeholder="Acme HQ / Lincoln Elementary"
                  minLength={2}
                  maxLength={160}
                />
              </Field>
              <Field label="What kind of building?">
                <select
                  value={form.company_type}
                  onChange={(e) => setForm((f) => ({ ...f, company_type: e.target.value }))}
                  data-testid="register-type-select"
                  className={inputCls}
                >
                  <option value="business">Office / Business</option>
                  <option value="school">School</option>
                  <option value="care">Personal Care Home / Adult Care</option>
                  <option value="daycare">Daycare / Pre-K</option>
                </select>
              </Field>
              <Field label="Your name">
                <input
                  required
                  value={form.admin_name}
                  onChange={set("admin_name")}
                  data-testid="register-name"
                  className={inputCls}
                  placeholder="Jane Smith"
                  minLength={2}
                />
              </Field>
              <Field label="Work email">
                <input
                  required
                  type="email"
                  value={form.admin_email}
                  onChange={set("admin_email")}
                  data-testid="register-email"
                  className={inputCls}
                  placeholder="jane@acme.com"
                />
              </Field>
              <button
                type="submit"
                disabled={submitting}
                data-testid="register-submit-btn"
                className="w-full h-11 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Create account & verify email
                <ArrowRight className="h-4 w-4" />
              </button>
              <div className="text-xs text-slate-500 text-center pt-2">
                Already have an account?{" "}
                <Link to="/login" className="text-primary hover:underline">Sign in</Link>
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm font-body text-slate-900 placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">{label}</span>
      {children}
      {hint && <span className="block mt-1 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
